import Reveal from './Reveal';
import { Code2, Terminal, BarChart4 } from 'lucide-react';

const ProfessionalTeams = () => {
    return (
        <section className="py-20 sm:py-28 relative" id="for-teams">
            {/* Subtle glow */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-purple-500/5 blur-[150px] rounded-full" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="text-center max-w-4xl mx-auto mb-16 sm:mb-20">
                    <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-mono mb-6 sm:mb-8">
                        For Professional Teams
                    </div>
                    <Reveal>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-6 sm:mb-8">
                            Built for{' '}
                            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Professional Teams</span>
                        </h2>
                        <p className="text-base sm:text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto">
                            Enterprise-grade API testing with an interface designed for speed and clarity. From simple requests to complex orchestrations.
                        </p>
                    </Reveal>
                </div>

                <div className="max-w-4xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-5">
                        {/* Request Builder Card */}
                        <Reveal className="h-full">
                            <div className="h-full bg-[#111111] rounded-xl border border-white/5 hover:border-cyan-500/20 transition-all duration-500 overflow-hidden group">
                                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0d0d0d] border-b border-white/5">
                                    <div className="flex gap-1">
                                        <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]/60" />
                                        <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]/60" />
                                        <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]/60" />
                                    </div>
                                    <span className="font-mono text-label text-gray-600 ml-1">request-builder.ts</span>
                                </div>
                                <div className="p-6 sm:p-8">
                                    <div className="flex items-start gap-4 sm:gap-5 mb-5">
                                        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 flex-shrink-0 group-hover:scale-110 transition-transform">
                                            <Code2 className="w-5 h-5 sm:w-6 sm:h-6" />
                                        </div>
                                        <div>
                                            <h4 className="text-lg sm:text-xl font-bold text-white mb-2 font-mono">Advanced Request Builder</h4>
                                            <p className="text-gray-500 leading-relaxed text-sm">
                                                Construct complex API requests with headers, script, body data, and authentication.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 pl-0 sm:pl-[60px]">
                                        {['Headers', 'Auth', 'Body', 'Scripts'].map(tag => (
                                            <span key={tag} className="px-3 py-1 rounded-md bg-white/5 text-label font-mono font-bold text-cyan-400/60 border border-white/5">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Reveal>

                        {/* Scripting Card */}
                        <Reveal delay={100} className="h-full">
                            <div className="h-full bg-[#111111] rounded-xl border border-white/5 hover:border-purple-500/20 transition-all duration-500 overflow-hidden group">
                                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0d0d0d] border-b border-white/5">
                                    <div className="flex gap-1">
                                        <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]/60" />
                                        <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]/60" />
                                        <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]/60" />
                                    </div>
                                    <span className="font-mono text-label text-gray-600 ml-1">automation.ts</span>
                                </div>
                                <div className="p-6 sm:p-8">
                                    <div className="flex items-start gap-4 sm:gap-5 mb-5">
                                        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 flex-shrink-0 group-hover:scale-110 transition-transform">
                                            <Terminal className="w-5 h-5 sm:w-6 sm:h-6" />
                                        </div>
                                        <div>
                                            <h4 className="text-lg sm:text-xl font-bold text-white mb-2 font-mono">Scripting & Automation</h4>
                                            <p className="text-gray-500 leading-relaxed text-sm">
                                                Write pre-request and post-response scripts in JavaScript. Set variables and validate responses.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="bg-[#0a0a0a] rounded-lg p-3 sm:p-4 font-mono text-xs sm:text-sm border border-white/5 overflow-x-auto">
                                        <span className="text-cyan-300">sp</span><span className="text-white">.</span><span className="text-cyan-300">collection</span><span className="text-white">.</span><span className="text-[#7dd3fc]">set</span><span className="text-white">(</span><span className="text-[#fbbf24]">"order_id"</span><span className="text-white">,</span> <span className="text-[#fbbf24]">"O001A"</span><span className="text-white">)</span><span className="text-gray-600">;</span>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    {/* Monitoring Card */}
                    <Reveal delay={200}>
                        <div className="bg-[#111111] rounded-xl border border-white/5 hover:border-orange-500/20 transition-all duration-500 overflow-hidden group">
                            <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0d0d0d] border-b border-white/5">
                                <div className="flex gap-1">
                                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]/60" />
                                    <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]/60" />
                                    <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]/60" />
                                </div>
                                <span className="font-mono text-label text-gray-600 ml-1">monitoring.ts</span>
                            </div>
                            <div className="p-6 sm:p-8">
                                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-5 mb-6">
                                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400 flex-shrink-0 group-hover:scale-110 transition-transform">
                                        <BarChart4 className="w-5 h-5 sm:w-6 sm:h-6" />
                                    </div>
                                    <div>
                                        <h4 className="text-lg sm:text-xl font-bold text-white mb-2 font-mono">Monitoring Scheduled Test</h4>
                                        <p className="text-gray-500 leading-relaxed text-sm">
                                            Monitor API response metrics effortlessly by scheduling tests. Track metrics like response times and success rates, with results delivered straight to your Test Plans page.
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                    {[
                                        { label: 'Uptime', value: '99.8%', color: 'text-emerald-400', border: 'border-emerald-500/20' },
                                        { label: 'Avg Time', value: '124ms', color: 'text-cyan-400', border: 'border-cyan-500/20' },
                                        { label: 'Tests/Day', value: '1.2K', color: 'text-purple-400', border: 'border-purple-500/20' },
                                    ].map((stat) => (
                                        <div key={stat.label} className={`bg-[#0a0a0a] rounded-lg p-3 sm:p-4 border ${stat.border} text-center`}>
                                            <p className="text-label sm:text-label text-gray-600 uppercase font-mono font-bold mb-1">{stat.label}</p>
                                            <p className={`text-sm sm:text-base font-bold font-mono ${stat.color}`}>{stat.value}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default ProfessionalTeams;
